import { useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { color, Icon, Input } from '@wf/components';
import { Grid } from '@wf/sw-ui-kit';

import Scrollable from '../../../../../components/ListWithContentLayout/components/Scrollable';
import UploadAttachmentsButton from '../UploadAttachmentsButton';
import AttachNewDocumentButton from '../AttachNewDocumentButton';
import { useLicenseContext } from '../../../LicenseModeContext';
import AttachmentCard from '../AttachmentCard';

import { type IAttachmentFormValues, type IAttachmentStateItem } from '../types';
import { ArrayFields } from '../../../types/fieldEnums';

import { attachmentPermissionsSelector } from '../../../store/selectors';
import { formatDate } from '../../../../../helpers/dateHelpers';

import styles from './AttachmentList.module.scss';

interface IAttachmentListProps {
  onUploadAttachments: (data: IAttachmentFormValues[]) => void;
  openImportFromParentModal: () => void;
  attachments: IAttachmentStateItem[];
  selectedAttachmentIndex: Nullable<number>;
  selectAttachment: (index: number) => void;
}

function AttachmentList({
  onUploadAttachments,
  attachments,
  selectedAttachmentIndex,
  selectAttachment,
  openImportFromParentModal,
}: IAttachmentListProps) {
  const { t } = useTranslation();
  const { viewMode } = useLicenseContext();

  const [search, setSearch] = useState('');

  const searchText = search.trim().toLowerCase();

  const permissions = useSelector(attachmentPermissionsSelector);

  const {
    formState: { errors },
  } = useFormContext();

  const showCreateButton = !viewMode && !!permissions?.add;

  const attachmentErrors = errors?.[ArrayFields.attachedDocuments];

  return (
    <>
      <Grid gap={8} className="p-b-8">
        <span className="fs-20 fw-semibold typography flex space-between m-b-8">{t('All Documents')}</span>
        {!!attachments.length && (
          <Input
            leftIcon={<Icon name="ic_search" color={color('typography', 'light')} size={15} />}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search')}
          />
        )}
        {showCreateButton && (
          <UploadAttachmentsButton
            button={
              // @ts-expect-error onClick prop will be passed via cloneElement in UploadAttachmentsButtonComponent
              <AttachNewDocumentButton
                onImport={openImportFromParentModal}
                leftIcon={<Icon name="ic_add" />}
                className={styles.uploadButton}
              />
            }
            onUploadAttachments={onUploadAttachments}
            showButtonTooltip
          />
        )}
      </Grid>
      <Scrollable>
        <Grid gap={8}>
          {attachments.map(({ id, code, referenceNumber, attachmentDate }, index) => {
            if (
              !searchText ||
              code?.value?.startsWith(searchText) ||
              code?.tag?.label?.toLowerCase().includes(searchText) ||
              referenceNumber?.toLowerCase().includes(searchText)
            ) {
              return (
                <AttachmentCard
                  isActive={index === selectedAttachmentIndex}
                  //@ts-expect-error
                  hasError={!!attachmentErrors?.[index]}
                  referenceNumber={referenceNumber}
                  date={formatDate(attachmentDate)}
                  onClick={selectAttachment}
                  index={index}
                  code={code}
                  key={id}
                />
              );
            }

            return null;
          })}
        </Grid>
      </Scrollable>
    </>
  );
}

export default AttachmentList;
